import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { docsApi, errorOf, LEVEL_LABEL, LEVEL_BLURB } from '../../lib/docs';
import { DocIcon } from './DocIcons';

/**
 * Who can see this space, folder or article - and, with nodeId 'root', the
 * firm-wide default every space inherits (Settings -> Documentation).
 *
 * Three tabs:
 *   Access              the rules on this node, what it inherits and from
 *                       where, the presets, and Save
 *   Who can see this?   pick a person: their level, the sentence saying why,
 *                       and the walk up the tree that found it
 *   Everyone            everybody who can see it, and how far
 *
 * The rule itself (lib/docs-access.js on the server) is summarised at the
 * foot, because a permissions screen that does not say how rules combine
 * is a screen people guess at.
 */

const KINDS = [
  { value: 'user', label: 'A member of staff', needs: 'user' },
  { value: 'contractor', label: 'A contractor', needs: 'contractor' },
  { value: 'group', label: 'A group', needs: 'group' },
  { value: 'tier', label: 'A contractor tier', needs: 'tier' },
  { value: 'company', label: 'A contractor’s company', needs: 'company' },
  { value: 'department', label: 'A department', needs: 'department' },
  { value: 'fixers', label: 'IT staff (staff who fix tickets)' },
  { value: 'all_staff', label: 'All staff' },
  { value: 'all_contractors', label: 'All contractors' }
];
const LEVELS = ['view', 'comment', 'edit', 'manage', 'deny'];
const SPEC_LABEL = { person: 'Person', group: 'Group', everybody: 'Everybody' };

const keyOf = (e) => [e.principal, (e.value || '').toLowerCase(), e.group_id || '', e.user_id || '', e.contractor_id || ''].join('|');

function LevelBadge({ level }) {
  return <span className={`dp-level dp-level-${level}`}>{LEVEL_LABEL[level] || level}</span>;
}

/** A searchable list of people, for the checker and the "Only these" preset. */
function PersonPicker({ principals, value, onChange, label = 'Person', only = null, id }) {
  const [q, setQ] = useState('');
  const people = useMemo(() => {
    const all = [
      ...(only === 'contractor' ? [] : (principals?.staff || []).map((p) => ({ ...p, sub: p.role === 'admin' ? 'Administrator' : p.it_staff ? 'IT staff' : (p.department || 'Staff') }))),
      ...(only === 'user' ? [] : (principals?.contractors || []).map((p) => ({ ...p, sub: [p.company, p.tier].filter(Boolean).join(' · ') || 'Contractor' })))
    ];
    const s = q.trim().toLowerCase();
    return s ? all.filter((p) => `${p.name} ${p.email || ''} ${p.sub}`.toLowerCase().includes(s)) : all;
  }, [principals, q, only]);
  return (
    <div className="dp-picker">
      <label className="dp-label" htmlFor={id}>{label}</label>
      <input id={id} className="dp-input" type="search" placeholder="Search by name or email" value={q}
             onChange={(e) => setQ(e.target.value)} />
      <ul className="dp-picker-list" role="listbox" aria-label={label}>
        {people.slice(0, 60).map((p) => (
          <li key={p.key}>
            <button type="button" role="option" aria-selected={value === p.key}
                    className={`dp-person${value === p.key ? ' is-on' : ''}`} onClick={() => onChange(p.key, p)}>
              <span className={`dp-avatar dp-avatar-${p.kind}`} aria-hidden="true">{(p.name || '?').slice(0, 1)}</span>
              <span className="dp-person-text"><span className="dp-person-name">{p.name}</span><span className="dp-person-sub">{p.sub}</span></span>
              {value === p.key && <DocIcon name="check" />}
            </button>
          </li>
        ))}
        {!people.length && <li className="dp-none">Nobody matches.</li>}
      </ul>
    </div>
  );
}

function Checker({ nodeId, principals }) {
  const [who, setWho] = useState('');
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!who) return undefined;
    let live = true;
    setRes(null); setErr(null);
    docsApi.get(`/docs/nodes/${nodeId}/check`, { person: who })
      .then((r) => { if (live) setRes(r); }).catch((e) => { if (live) setErr(errorOf(e)); });
    return () => { live = false; };
  }, [who, nodeId]);
  return (
    <div className="dp-check">
      <PersonPicker principals={principals} value={who} onChange={setWho} label="Check a person" id={`dp-check-${nodeId}`} />
      <div className="dp-check-result" aria-live="polite">
        {!who && <p className="dp-hint">Pick somebody to see exactly what they can do here, and which rule decided it.</p>}
        {err && <p className="dp-error" role="alert">{err}</p>}
        {res && (
          <div className="dp-verdict">
            <div className="dp-verdict-head">
              <strong>{res.person.name}</strong>
              <LevelBadge level={res.level} />
            </div>
            <p className="dp-person-facts">
              {[res.person.is_admin ? 'Administrator' : res.person.kind === 'contractor' ? 'Contractor' : 'Staff',
                res.person.it_staff ? 'IT staff' : null, res.person.department, res.person.company, res.person.tier ? `tier ${res.person.tier}` : null,
                ...(res.person.groups || []).map((g) => `in “${g.name}”`)].filter(Boolean).join(' · ')}
            </p>
            <p className="dp-because">{res.because}</p>
            {res.note && <p className="dp-note"><DocIcon name="info" />{res.note}</p>}
            {res.path && (
              <ol className="dp-walk" aria-label="How it was worked out">
                {res.path.map((p) => (
                  <li key={p.id} className={p.matched.length ? 'is-hit' : ''}>
                    <span className="dp-walk-title">{p.title}<span className="dp-walk-kind">{p.kind}</span></span>
                    <span className="dp-walk-rules">
                      {p.matched.length ? p.matched.map((m, i) => <span key={i} className="dp-walk-rule">{m.label} → {LEVEL_LABEL[m.level]}</span>)
                        : <span className="dp-walk-none">{p.breaks ? 'nothing for them, and does not inherit' : 'nothing for them'}</span>}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Audience({ nodeId }) {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    docsApi.get(`/docs/nodes/${nodeId}/audience`).then((r) => { if (live) setRes(r); }).catch((e) => { if (live) setErr(errorOf(e)); });
    return () => { live = false; };
  }, [nodeId]);
  if (err) return <p className="dp-error" role="alert">{err}</p>;
  if (!res) return <p className="dp-hint" aria-busy="true">Working it out…</p>;
  return (
    <div className="dp-aud">
      <p className="dp-hint">{res.counts.staff} staff and {res.counts.contractors} contractor{res.counts.contractors === 1 ? '' : 's'} can see this. Administrators can always see everything.</p>
      <ul className="dp-aud-list">
        {res.people.map((p) => (
          <li key={p.key}>
            <span className={`dp-avatar dp-avatar-${p.kind === 'contractor' ? 'contractor' : 'user'}`} aria-hidden="true">{p.name.slice(0, 1)}</span>
            <span className="dp-aud-text"><span className="dp-person-name">{p.name}</span><span className="dp-person-sub">{p.because}</span></span>
            <LevelBadge level={p.level} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function DocPermissions({ nodeId, title, kind = 'space', onSaved, embedded = false }) {
  const isRoot = nodeId === 'root';
  const [tab, setTab] = useState('access');
  const [data, setData] = useState(null);
  const [principals, setPrincipals] = useState(null);
  const [entries, setEntries] = useState([]);
  const [brk, setBrk] = useState(false);
  const [askBreak, setAskBreak] = useState(false);
  const [copyInherited, setCopyInherited] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState({ principal: 'contractor', level: 'view', ref: '' });
  const [only, setOnly] = useState(null); // contractor keys for "Only these"

  const load = useCallback(async () => {
    try {
      const [p, pr] = await Promise.all([docsApi.get(`/docs/nodes/${nodeId}/permissions`), docsApi.get('/docs/principals')]);
      setData(p); setPrincipals(pr);
      setEntries(p.entries); setBrk(Boolean(p.break_inheritance)); setCopyInherited(false); setAskBreak(false);
    } catch (e) { setErr(errorOf(e, 'Could not load the permissions')); }
  }, [nodeId]);
  useEffect(() => { load(); }, [load]);

  const dirty = data && (brk !== Boolean(data.break_inheritance)
    || JSON.stringify(entries.map((e) => [keyOf(e), e.level])) !== JSON.stringify(data.entries.map((e) => [keyOf(e), e.level])));

  const labelFor = useCallback((e) => {
    if (e.label) return e.label;
    const find = (list, id) => (list || []).find((x) => x.id === id)?.name;
    switch (e.principal) {
      case 'user': return find(principals?.staff, e.user_id) || 'A member of staff';
      case 'contractor': return find(principals?.contractors, e.contractor_id) || 'A contractor';
      case 'group': return `Group: ${find(principals?.groups, e.group_id) || 'a group'}`;
      case 'tier': return `Contractor tier: ${e.value}`;
      case 'company': return `Company: ${e.value}`;
      case 'department': return `Department: ${e.value}`;
      default: return KINDS.find((k) => k.value === e.principal)?.label || e.principal;
    }
  }, [principals]);
  const specOf = (p) => (['user', 'contractor'].includes(p) ? 'person' : ['all_staff', 'all_contractors'].includes(p) ? 'everybody' : 'group');

  const kindDef = KINDS.find((k) => k.value === adding.principal);
  const refOptions = useMemo(() => {
    switch (kindDef?.needs) {
      case 'user': return (principals?.staff || []).map((p) => ({ value: p.id, label: `${p.name}${p.it_staff ? ' (IT)' : ''}` }));
      case 'contractor': return (principals?.contractors || []).map((p) => ({ value: p.id, label: `${p.name}${p.company ? ` - ${p.company}` : ''}` }));
      case 'group': return (principals?.groups || []).map((g) => ({ value: g.id, label: g.name }));
      case 'tier': return (principals?.tiers || []).map((t) => ({ value: t, label: t[0].toUpperCase() + t.slice(1) }));
      case 'company': return (principals?.companies || []).map((c) => ({ value: c, label: c }));
      case 'department': return (principals?.departments || []).map((d) => ({ value: d, label: d }));
      default: return [];
    }
  }, [kindDef, principals]);

  const add = () => {
    setErr(null);
    const e = { principal: adding.principal, level: adding.level, value: null, group_id: null, user_id: null, contractor_id: null };
    if (kindDef?.needs) {
      if (!adding.ref) { setErr('Choose who this rule is for'); return; }
      if (kindDef.needs === 'user') e.user_id = adding.ref;
      else if (kindDef.needs === 'contractor') e.contractor_id = adding.ref;
      else if (kindDef.needs === 'group') e.group_id = adding.ref;
      else e.value = adding.ref;
    }
    if (entries.some((x) => keyOf(x) === keyOf(e))) { setErr('That is already on the list - change its level instead'); return; }
    setEntries([...entries, { ...e, label: undefined }]);
    setAdding({ ...adding, ref: '' });
  };

  const send = async (body) => {
    setBusy(true); setErr(null); setSaved(null);
    try {
      const r = await docsApi.put(`/docs/nodes/${nodeId}/permissions`, body);
      setData(r); setEntries(r.entries); setBrk(Boolean(r.break_inheritance)); setAskBreak(false); setOnly(null); setCopyInherited(false);
      setSaved('Saved. It applies at once.');
      if (onSaved) onSaved(r);
    } catch (e) { setErr(errorOf(e, 'Could not save the permissions')); }
    setBusy(false);
  };
  const save = () => send({
    break_inheritance: brk,
    copy_inherited: copyInherited,
    entries: entries.map(({ principal, level, value, group_id: g, user_id: u, contractor_id: c }) => ({ principal, level, value, group_id: g, user_id: u, contractor_id: c }))
  });

  const toggleInherit = () => {
    if (!brk) { setAskBreak(true); return; }
    setBrk(false); setCopyInherited(false);
  };

  if (!data && !err) return <div className="dp" aria-busy="true"><p className="dp-hint">Loading the permissions…</p></div>;

  return (
    <div className={`dp${embedded ? ' is-embedded' : ''}`}>
      <div className="dp-tabs" role="tablist" aria-label="Permissions">
        {[['access', isRoot ? 'Default access' : 'Access'], ['check', 'Who can see this?'], ...(isRoot ? [] : [['audience', 'Everyone with access']])].map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} className={`dp-tab${tab === id ? ' is-on' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {tab === 'check' && <Checker nodeId={nodeId} principals={principals} />}
      {tab === 'audience' && <Audience nodeId={nodeId} />}

      {tab === 'access' && data && (
        <div className="dp-access">
          {!isRoot && (
            <div className="dp-inherit">
              <div className="dp-inherit-text">
                <strong>{brk ? 'Its own permissions' : 'Inherits permissions'}</strong>
                <span>{brk ? `Nothing above this ${kind} applies to it - only the rules below.` : `Rules from above apply, and the rules below add to or override them here.`}</span>
              </div>
              <button type="button" role="switch" aria-checked={!brk} aria-label="Inherit permissions from above"
                      className={`dp-switch${!brk ? ' is-on' : ''}`} onClick={toggleInherit}><span /></button>
            </div>
          )}
          {askBreak && (
            <div className="dp-ask" role="group" aria-label="Stop inheriting">
              <p>Stop inheriting? Start from what applies now, or from nothing?</p>
              <div className="dp-ask-actions">
                <button type="button" className="btn btn-primary btn-small" onClick={() => { setBrk(true); setCopyInherited(true); setAskBreak(false); }}>Copy what applies now</button>
                <button type="button" className="btn btn-outline btn-small" onClick={() => { setBrk(true); setCopyInherited(false); setAskBreak(false); }}>Start empty</button>
                <button type="button" className="btn btn-plain btn-small" onClick={() => setAskBreak(false)}>Keep inheriting</button>
              </div>
            </div>
          )}
          {brk && copyInherited && !data.break_inheritance && <p className="dp-hint"><DocIcon name="info" />The inherited rules will be copied here when you save.</p>}

          {!isRoot && (
            <div className="dp-presets">
              <span className="dp-label">Quick settings</span>
              <div className="dp-preset-row">
                <button type="button" className="dp-preset" disabled={busy} onClick={() => send({ preset: 'all_contractors' })}>
                  <DocIcon name="users" /><span><strong>Open to all contractors</strong><small>Adds “All contractors: View”</small></span>
                </button>
                <button type="button" className="dp-preset" disabled={busy} onClick={() => send({ preset: 'staff_only' })}>
                  <DocIcon name="lock" /><span><strong>Staff only</strong><small>Its own rules: staff read, IT staff edit</small></span>
                </button>
                <button type="button" className={`dp-preset${only ? ' is-on' : ''}`} disabled={busy} onClick={() => setOnly(only ? null : [])}>
                  <DocIcon name="user" /><span><strong>Only these contractors…</strong><small>Staff, plus the contractors you pick</small></span>
                </button>
              </div>
              {only && (
                <div className="dp-only">
                  <div className="dp-only-list">
                    {(principals?.contractors || []).map((c) => (
                      <label key={c.id} className="dp-only-item">
                        <input type="checkbox" checked={only.includes(c.id)}
                               onChange={(e) => setOnly(e.target.checked ? [...only, c.id] : only.filter((x) => x !== c.id))} />
                        <span>{c.name}<small>{[c.company, c.tier].filter(Boolean).join(' · ')}</small></span>
                      </label>
                    ))}
                  </div>
                  <button type="button" className="btn btn-primary btn-small" disabled={!only.length || busy}
                          onClick={() => send({ preset: 'only_these', contractors: only })}>Apply for {only.length} contractor{only.length === 1 ? '' : 's'}</button>
                </div>
              )}
            </div>
          )}

          <div className="dp-rules">
            <span className="dp-label">{isRoot ? 'Firm-wide rules' : `Rules on this ${kind}`}</span>
            {!entries.length && <p className="dp-hint">No rules here{brk ? ' - only administrators can see it.' : ' - it follows what it inherits.'}</p>}
            <ul className="dp-rule-list">
              {entries.map((e, i) => (
                <li key={keyOf(e)} className={`dp-rule${e.level === 'deny' ? ' is-deny' : ''}`}>
                  <span className={`dp-spec dp-spec-${specOf(e.principal)}`}>{SPEC_LABEL[specOf(e.principal)]}</span>
                  <span className="dp-rule-who">{labelFor(e)}</span>
                  <select className="dp-select" aria-label={`Level for ${labelFor(e)}`} value={e.level}
                          onChange={(ev) => setEntries(entries.map((x, j) => (j === i ? { ...x, level: ev.target.value } : x)))}>
                    {LEVELS.map((l) => <option key={l} value={l}>{LEVEL_LABEL[l]}</option>)}
                  </select>
                  <button type="button" className="dp-remove" aria-label={`Remove ${labelFor(e)}`} onClick={() => setEntries(entries.filter((_, j) => j !== i))}>
                    <DocIcon name="close" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="dp-add" role="group" aria-label="Add a rule">
              <select className="dp-select" aria-label="Who" value={adding.principal} onChange={(e) => setAdding({ ...adding, principal: e.target.value, ref: '' })}>
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
              {kindDef?.needs && (
                <select className="dp-select dp-select-grow" aria-label="Which" value={adding.ref} onChange={(e) => setAdding({ ...adding, ref: e.target.value })}>
                  <option value="">Choose…</option>
                  {refOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
              <select className="dp-select" aria-label="Level" value={adding.level} onChange={(e) => setAdding({ ...adding, level: e.target.value })}>
                {LEVELS.map((l) => <option key={l} value={l}>{LEVEL_LABEL[l]}</option>)}
              </select>
              <button type="button" className="btn btn-secondary btn-small" onClick={add}><DocIcon name="plus" />Add</button>
            </div>
            <p className="dp-level-help">{LEVEL_BLURB[adding.level]}</p>
          </div>

          {!brk && data.inherited.length > 0 && (
            <div className="dp-inherited">
              <span className="dp-label">Inherited</span>
              {data.inherited.map((layer) => (
                <div key={layer.from.id} className="dp-layer">
                  <span className="dp-layer-from">From {layer.from.kind === 'default' ? 'the firm-wide default' : `the ${layer.from.kind} “${layer.from.title}”`}</span>
                  <ul>
                    {layer.entries.map((e) => (
                      <li key={keyOf(e)}><span className="dp-rule-who">{e.label}</span><LevelBadge level={e.level} /></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {err && <p className="dp-error" role="alert">{err}</p>}
          {saved && !dirty && <p className="dp-saved" role="status"><DocIcon name="check" />{saved}</p>}
          <div className="dp-foot">
            <button type="button" className="btn btn-primary" disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save permissions'}</button>
            {dirty && <button type="button" className="btn btn-plain" onClick={() => { setEntries(data.entries); setBrk(Boolean(data.break_inheritance)); setCopyInherited(false); }}>Undo changes</button>}
          </div>

          <details className="dp-rulebook">
            <summary>How rules combine</summary>
            <ol>
              <li>Administrators can always manage everything.</li>
              <li>Jarvis starts at the item and walks up through its folders to the space, then the firm-wide default, and stops at the <strong>first</strong> level with a rule for the person.</li>
              <li>There, a rule for <strong>the person</strong> beats one for <strong>a group</strong> (group, tier, company, department, IT staff), which beats one for <strong>everybody</strong>.</li>
              <li>Among rules of the same kind, <strong>No access</strong> beats any allow; otherwise the highest level wins.</li>
              <li>An item that does not inherit stops the walk: nothing above it counts.</li>
              <li>Readers below Edit only ever see the published version; drafts are for editors.</li>
            </ol>
          </details>
        </div>
      )}
      {tab === 'access' && !data && err && <p className="dp-error" role="alert">{err}</p>}
      {title && <span className="dp-sr">{title}</span>}
    </div>
  );
}
